'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { IBirthdayBanner, IBirthdayBannerTier } from '@/sanity/lib/definitions';
import { DEFAULT_LANGUAGE, resolveI18nText } from '@/sanity/globals/I18n';
import { cn } from '@/lib/utils';
import { resolveLink } from '@/sanity/globals/CTA';
import styles from '@/scss/global/BirthdayTopBanner.module.scss';

interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

function getTimeLeft(endDate: Date): TimeLeft {
  const diff = endDate.getTime() - Date.now();
  if (diff <= 0) return { hours: 0, minutes: 0, seconds: 0, expired: true };
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { hours, minutes, seconds, expired: false };
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function parsePopupHeadline(text: string) {
  const parts = text.split(/\[\[(.+?)\]\]/g);
  return (
    <div className="flex flex-wrap items-center justify-center gap-[1.5rem] text-center">
      {parts.map((part, i) => {
        if (!part.trim()) return null;
        if (i % 2 === 1) {
          return (
            <span
              key={i}
              className="inline-flex flex-col items-center shrink-0 relative"
            >
              <span
                className="font-blacker italic text-[4rem] md:text-[6.5rem] leading-[1.12] tracking-[-0.2rem] relative z-10"
                style={{ color: '#d6eee7' }}
              >
                {part}
              </span>
              <span
                className="w-full h-[2.5rem] md:h-[5rem] absolute bottom-[0rem] left-0"
                style={{ background: 'rgba(34,142,113,0.13)' }}
              />
            </span>
          );
        }
        return (
          <span
            key={i}
            className="font-geist font-semibold text-[4rem] md:text-[6rem] leading-[0.95] tracking-[-0.3rem]"
            style={{ color: '#fefefe' }}
          >
            {part.trim()}
          </span>
        );
      })}
    </div>
  );
}


interface BirthdayPopUpProps {
  data: IBirthdayBanner;
  ctaUrl: string;
  language?: string;
}

export default function BirthdayPopUp({
  data,
  ctaUrl,
  language,
}: BirthdayPopUpProps) {
  const [open, setOpen] = useState(false);
  const endDate = useMemo(
    () => (data.countdownEndDate ? new Date(data.countdownEndDate) : null),
    [data.countdownEndDate],
  );
  const link = resolveLink(ctaUrl, language || DEFAULT_LANGUAGE);
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  useEffect(() => {
    if (!endDate) return;
    setTimeLeft(getTimeLeft(endDate));
    const interval = setInterval(() => setTimeLeft(getTimeLeft(endDate)), 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 0.85) {
        setOpen(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = 'unset';
  }, [open]);

  if (endDate && timeLeft?.expired) return null;

  const endsInLabel = resolveI18nText(
    data.endsInLabel ?? 'ENDS IN',
    data.endsInLabelI18n,
    language,
  );
  const hoursLabel = resolveI18nText(
    data.hoursLabel ?? 'HRS',
    data.hoursLabelI18n,
    language,
  );
  const minutesLabel = resolveI18nText(
    data.minutesLabel ?? 'MIN',
    data.minutesLabelI18n,
    language,
  );
  const secondsLabel = resolveI18nText(
    data.secondsLabel ?? 'SEC',
    data.secondsLabelI18n,
    language,
  );
  const popupCtaLabel = resolveI18nText(
    data.popupCtaLabel,
    data.popupCtaLabelI18n,
    language,
  );
  const ctaLabel =
    popupCtaLabel ||
    resolveI18nText(data.headerCtaLabel, data.headerCtaLabelI18n, language);
  const popupText = resolveI18nText(
    data.popupMainText,
    data.popupMainTextI18n,
    language,
  );
  const mainText =
    popupText ||
    resolveI18nText(data.headerMainText, data.headerMainTextI18n, language);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="!w-[min(75rem,calc(100%-1.5rem))] max-h-[95dvh] overflow-y-auto p-0 border-0 !duration-700 ease-in rounded-[2rem] md:rounded-[2.5rem]"
        style={{ background: '#001b14' }}
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle />
          </DialogHeader>
        </VisuallyHidden>

        <div className="flex flex-col">
          {/* Countdown */}
          {endDate && timeLeft && (
            <div className="flex flex-col items-center gap-[1.25rem] md:gap-[1.875rem] pt-[2.5rem] md:pt-[4rem] pb-[1.5rem] md:pb-[2rem] px-[2rem] md:px-[4rem]">
              <span
                className="font-mono text-[1.625rem] md:text-[2rem] font-normal tracking-[-0.1rem] text-center"
                style={{ color: '#1aa87f' }}
              >
                {endsInLabel}
              </span>
              <div className="flex items-center gap-[1rem] md:gap-[1.25rem] w-full max-w-[33.6rem]">
                <PopupCountdownBox value={pad(timeLeft.hours)} />
                <span
                  className="font-semibold text-[2.25rem] md:text-[2.75rem] leading-[0.95] tracking-[-0.1rem] shrink-0"
                  style={{ color: '#bfe2d8' }}
                >
                  :
                </span>
                <PopupCountdownBox value={pad(timeLeft.minutes)} />
                <span
                  className="font-semibold text-[2.25rem] md:text-[2.75rem] leading-[0.95] tracking-[-0.1rem] shrink-0"
                  style={{ color: '#bfe2d8' }}
                >
                  :
                </span>
                <PopupCountdownBox value={pad(timeLeft.seconds)} />
              </div>
              <div className="flex items-center justify-between w-full max-w-[29rem] px-[0.25rem]">
                <span
                  className="font-mono text-[1.375rem] md:text-[1.625rem] font-normal tracking-[-0.065rem] text-center flex-1"
                  style={{ color: '#7a7a7a' }}
                >
                  {hoursLabel}
                </span>
                <span
                  className="font-mono text-[1.375rem] md:text-[1.625rem] font-normal tracking-[-0.065rem] text-center flex-1"
                  style={{ color: '#7a7a7a' }}
                >
                  {minutesLabel}
                </span>
                <span
                  className="font-mono text-[1.375rem] md:text-[1.625rem] font-normal tracking-[-0.065rem] text-center flex-1"
                  style={{ color: '#7a7a7a' }}
                >
                  {secondsLabel}
                </span>
              </div>
            </div>
          )}

          {/* Headline */}
          {mainText && (
            <div className="flex flex-col items-center px-[2rem] md:px-[6rem] pb-[1.5rem] md:pb-[2.5rem]">
              {parsePopupHeadline(mainText)}
            </div>
          )}

          {/* Tiers */}
          {data.tiers && data.tiers.length > 0 && (
            <div className="flex flex-col px-[1.5rem] md:px-[4rem] gap-[1.5rem] md:gap-[2.75rem] pb-[0.5rem]">
              {data.tiers.map((tier, i) => (
                <PopupTierRow key={i} tier={tier} language={language} />
              ))}
            </div>
          )}

          {/* CTA bar */}
          <div
            className="flex items-center justify-center px-[1.5rem] md:px-[5rem] py-[2rem] md:py-[3rem] mt-[1.5rem] md:mt-[2rem]"
            style={{
              background: '#082a20',
              borderTop: '0.5px solid rgba(219,219,219,0.1)',
            }}
          >
            <Link
              href={finalLink ?? ctaUrl}
              className="w-full flex items-center justify-center gap-[2rem] px-[2rem] md:px-[2.875rem] py-[1.75rem] md:py-[2rem] rounded-[0.5rem] font-mono text-[2rem] font-semibold tracking-[0.04em] uppercase hover:brightness-95 transition-all"
              style={{
                background: '#ffffff',
                color: '#00110c',
                boxShadow:
                  'inset 1.425px 0px 4.66px rgba(255,255,255,0.45), inset 0.491px 1.472px 3.386px rgba(0,0,0,0.1)',
              }}
            >
              {ctaLabel}
              <svg
                className="shrink-0 w-[2.5rem] h-[2.5rem]"
                viewBox="0 0 16 16"
                fill="#00110c"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.7833 8.66667H2.66667V7.33333H10.7833L7.05 3.6L8 2.66667L13.3333 8L8 13.3333L7.05 12.4L10.7833 8.66667Z" />
              </svg>
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PopupCountdownBox({ value }: { value: string }) {
  return (
    <div
      className={cn(
        'flex-1 flex items-center justify-center overflow-hidden py-[1.25rem] md:py-[2.25rem] min-w-0',
        styles.countdownBox,
      )}
      style={{
        background:
          'linear-gradient(to bottom, rgba(0,182,122,0), rgba(0,182,122,0.1))',
        border: '1.14px solid rgba(0,182,122,0.2)',
      }}
    >
      <span
        className="font-semibold tabular-nums text-[2.25rem] md:text-[3.25rem] leading-[0.95] tracking-[-0.165rem] text-center w-full"
        style={{ color: '#bfe2d8' }}
      >
        {value}
      </span>
    </div>
  );
}

function PopupTierRow({
  tier,
  language,
}: {
  tier: IBirthdayBannerTier;
  language?: string;
}) {
  const label = resolveI18nText(tier.label, tier.labelI18n, language);
  const planName = resolveI18nText(tier.planName, tier.planNameI18n, language);
  const savingsText = resolveI18nText(
    tier.savingsText,
    tier.savingsTextI18n,
    language,
  );
  const badgeLabel = resolveI18nText(
    tier.badgeLabel,
    tier.badgeLabelI18n,
    language,
  );
  const isHighlighted = !!tier.highlighted;

  const labelParts = label?.split(' ') ?? [];
  const badgeTop = labelParts[0] ?? '';
  const badgeBottom = labelParts.slice(1).join(' ');

  return (
    <div
      className={cn(
        'relative flex items-center gap-[1.25rem] md:gap-[2rem] px-[1.5rem] md:px-[2rem] py-[1.5rem] md:py-[1.625rem]',
        styles.tierChip,
      )}
      style={{
        background: isHighlighted
          ? 'linear-gradient(to bottom, rgba(0,182,122,0), rgba(0,182,122,0.7))'
          : 'linear-gradient(to bottom, rgba(0,182,122,0), rgba(0,182,122,0.1))',
      }}
    >
      {/* Floating badge (BEST OFFER) */}
      {isHighlighted && badgeLabel && (
        <div
          className="absolute -top-[1.625rem] right-[1.5rem] md:right-[3rem] flex items-center gap-[0.625rem] px-[1.25rem] py-[0.5rem]"
          style={{
            background: '#d0bd81',
            boxShadow: '0 0 8.9px rgba(208,189,129,0.5)',
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="#00110c"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M5 0L6.12 3.45H9.76L6.82 5.58L7.94 9.04L5 6.91L2.06 9.04L3.18 5.58L0.24 3.45H3.88L5 0Z" />
          </svg>
          <span
            className="font-mono text-[1.25rem] font-normal tracking-[-0.075rem] whitespace-nowrap"
            style={{ color: '#00110c' }}
          >
            {badgeLabel}
          </span>
        </div>
      )}

      {/* Left badge */}
      <div
        className="flex flex-col items-center justify-center px-[0.875rem] md:px-[1.25rem] py-[0.875rem] md:py-[1.25rem] gap-[0.25rem] shrink-0 min-w-[7.5rem] md:min-w-[9.5rem] self-stretch"
        style={{
          background: isHighlighted
            ? 'linear-gradient(to top, rgba(0,182,122,0.6), rgba(167,255,226,0.6))'
            : 'linear-gradient(to top, rgba(0,182,122,0.1), rgba(0,182,122,0))',
          border: '1.185px solid rgba(0,182,122,0.3)',
        }}
      >
        <span
          className="font-semibold text-[1.75rem] md:text-[2rem] leading-[0.95] tracking-[-0.1rem]"
          style={{ color: isHighlighted ? '#00110c' : '#bfe2d8' }}
        >
          {badgeTop}
        </span>
        <span
          className="font-mono text-[1rem] md:text-[1.25rem] font-normal tracking-[-0.065rem]"
          style={{ color: isHighlighted ? '#00110c' : '#1aa87f' }}
        >
          {badgeBottom}
        </span>
      </div>

      {/* Center: plan name + savings */}
      {(planName || savingsText) && (
        <div className="flex flex-col gap-[0.5rem] flex-1 min-w-0">
          {planName && (
            <span
              className="font-semibold text-[2.5rem] md:text-[3rem] leading-[0.95] tracking-[-0.19rem] whitespace-nowrap"
              style={{ color: '#bfe2d8' }}
            >
              {planName}
            </span>
          )}
          {savingsText && (
            <span
              className="font-semibold text-[1.5rem] md:text-[1.75rem] leading-[0.95] tracking-[-0.1rem]"
              style={{ color: '#d0bd81' }}
            >
              {savingsText}
            </span>
          )}
        </div>
      )}

      {/* Right discount */}
      <span
        className="font-blacker italic text-[3rem] md:text-[4.5rem] leading-[0.95] tracking-[-0.13rem] shrink-0 ml-auto"
        style={{ color: '#d6eee7' }}
      >
        {tier.discount}
      </span>
    </div>
  );
}
